/**
 * Control Node Executors
 *
 * Handles local execution of control flow nodes:
 * - wait: Pause for a duration
 * - condition: Branch based on condition (evaluates and sets context flag)
 * - loop: Iterate over items (processes items sequentially)
 * - parallel: Execute branches (marks for parallel execution)
 * - checkpoint: Save workflow state
 * - notify: Send notifications
 * - custom-script: Run custom code (sandboxed)
 */

/**
 * Wait Executor - Pause workflow for specified duration
 */
export const waitExecutor = {
  async execute(config, context, onProgress = () => {}, signal = null) {
    const duration = config.duration || 5
    const unit = config.unit || 'seconds'
    const reason = config.reason || ''

    // Convert to milliseconds
    let ms = duration * 1000
    if (unit === 'minutes') ms = duration * 60 * 1000
    if (unit === 'hours') ms = duration * 60 * 60 * 1000

    onProgress(0, `Waiting ${duration} ${unit}${reason ? `: ${reason}` : ''}...`)

    // Check for abort before waiting
    if (signal?.aborted) {
      throw new Error('Execution cancelled')
    }

    // Wait with progress updates
    const startTime = Date.now()
    const updateInterval = Math.min(ms / 10, 1000) // Update every 10% or 1 second

    await new Promise((resolve, reject) => {
      const checkAbort = () => {
        if (signal?.aborted) {
          reject(new Error('Execution cancelled'))
          return true
        }
        return false
      }

      const updateProgress = () => {
        if (checkAbort()) return

        const elapsed = Date.now() - startTime
        const progress = Math.min((elapsed / ms) * 100, 100)
        const remaining = Math.max(0, Math.ceil((ms - elapsed) / 1000))
        onProgress(progress, `Waiting... ${remaining}s remaining`)

        if (elapsed >= ms) {
          resolve()
        } else {
          setTimeout(updateProgress, updateInterval)
        }
      }

      updateProgress()
    })

    onProgress(100, 'Wait complete')

    return {
      success: true,
      duration_ms: ms,
      reason,
    }
  }
}

/**
 * Condition Executor - Evaluate condition and set branch flag
 *
 * Note: The actual branching is handled by the WorkflowExecutor
 * which checks context.branchConditions to skip/include nodes
 */
export const conditionExecutor = {
  async execute(config, context, onProgress = () => {}, signal = null) {
    const field = config.field || ''
    const operator = config.operator || 'eq'
    const value = config.value

    onProgress(0, 'Evaluating condition...')

    if (signal?.aborted) {
      throw new Error('Execution cancelled')
    }

    // Get the value to compare from context.results (previous node outputs)
    let fieldValue = null

    // Try to find the field in the most recent results
    const resultsKeys = Object.keys(context.results || {})
    for (const key of resultsKeys.reverse()) {
      const result = context.results[key]
      if (result && typeof result === 'object') {
        if (field in result) {
          fieldValue = result[field]
          break
        }
        // Check nested
        if (result.data && field in result.data) {
          fieldValue = result.data[field]
          break
        }
      }
    }

    // Also check jobs
    if (fieldValue === null && field.includes('job')) {
      fieldValue = context.jobs?.[field]
    }

    // Evaluate condition
    let conditionResult = false
    const compareValue = value

    switch (operator) {
      case 'eq':
        conditionResult = fieldValue == compareValue
        break
      case 'neq':
        conditionResult = fieldValue != compareValue
        break
      case 'gt':
        conditionResult = Number(fieldValue) > Number(compareValue)
        break
      case 'gte':
        conditionResult = Number(fieldValue) >= Number(compareValue)
        break
      case 'lt':
        conditionResult = Number(fieldValue) < Number(compareValue)
        break
      case 'lte':
        conditionResult = Number(fieldValue) <= Number(compareValue)
        break
      case 'contains':
        conditionResult = String(fieldValue).includes(String(compareValue))
        break
      case 'exists':
        conditionResult = fieldValue !== null && fieldValue !== undefined
        break
      case 'empty':
        conditionResult = !fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0)
        break
      default:
        conditionResult = Boolean(fieldValue)
    }

    onProgress(100, `Condition evaluated: ${conditionResult ? 'TRUE' : 'FALSE'}`)

    // Store the branch result in context for WorkflowExecutor to use
    if (!context.branchConditions) {
      context.branchConditions = {}
    }

    return {
      success: true,
      condition: conditionResult,
      field,
      operator,
      value: compareValue,
      fieldValue,
      branch: conditionResult ? 'true' : 'false',
    }
  }
}

/**
 * Loop Executor - Iterate over items
 *
 * Note: This is a simplified version that processes all items
 * and stores results. Full loop support would require executor changes.
 */
export const loopExecutor = {
  async execute(config, context, onProgress = () => {}, signal = null) {
    const maxIterations = config.maxIterations || 100
    const parallel = config.parallel || false
    const batchSize = config.batchSize || 5

    onProgress(0, 'Starting loop...')

    if (signal?.aborted) {
      throw new Error('Execution cancelled')
    }

    // Find items from previous node results
    let items = []
    const resultsKeys = Object.keys(context.results || {})
    for (const key of resultsKeys.reverse()) {
      const result = context.results[key]
      if (Array.isArray(result)) {
        items = result
        break
      }
      if (result?.items && Array.isArray(result.items)) {
        items = result.items
        break
      }
      if (result?.data && Array.isArray(result.data)) {
        items = result.data
        break
      }
    }

    // Limit iterations
    const processItems = items.slice(0, maxIterations)
    const totalItems = processItems.length

    onProgress(10, `Processing ${totalItems} items...`)

    // For now, just iterate and collect - downstream nodes would process each
    const results = []
    for (let i = 0; i < totalItems; i++) {
      if (signal?.aborted) {
        throw new Error('Execution cancelled')
      }

      const progress = 10 + ((i + 1) / totalItems) * 80
      onProgress(progress, `Processing item ${i + 1} of ${totalItems}`)

      results.push({
        index: i,
        item: processItems[i],
      })
    }

    onProgress(100, `Loop complete: ${totalItems} items processed`)

    return {
      success: true,
      itemCount: totalItems,
      items: processItems,
      results,
    }
  }
}

/**
 * Parallel Executor - Mark branches for parallel execution
 *
 * Note: Actual parallel execution requires WorkflowExecutor changes.
 * This executor signals that downstream branches can run in parallel.
 */
export const parallelExecutor = {
  async execute(config, context, onProgress = () => {}, signal = null) {
    const branches = config.branches || 2
    const waitForAll = config.waitForAll !== false

    onProgress(0, `Preparing ${branches} parallel branches...`)

    if (signal?.aborted) {
      throw new Error('Execution cancelled')
    }

    // Store parallel execution metadata in context
    if (!context.parallelBranches) {
      context.parallelBranches = {}
    }

    onProgress(100, `Parallel node ready: ${branches} branches`)

    return {
      success: true,
      branches,
      waitForAll,
      message: `Ready to execute ${branches} branches ${waitForAll ? 'and wait for all' : ''}`,
    }
  }
}

/**
 * Checkpoint Executor - Save workflow state
 */
export const checkpointExecutor = {
  async execute(config, context, onProgress = () => {}, signal = null) {
    const name = config.name || 'checkpoint'
    const description = config.description || ''

    onProgress(0, `Saving checkpoint: ${name}...`)

    if (signal?.aborted) {
      throw new Error('Execution cancelled')
    }

    // Create checkpoint data
    const checkpointData = {
      name,
      description,
      timestamp: new Date().toISOString(),
      context: {
        scout_account_id: context.scout_account_id,
        application_name: context.application_name,
        jobs: { ...context.jobs },
        source_hash: context.source_hash,
        // Don't save full results - too large
        resultKeys: Object.keys(context.results || {}),
      },
    }

    // Save to localStorage for now (could be API call in future)
    try {
      const key = `workflow_checkpoint_${context.scout_account_id}_${context.application_name}_${name}`
      localStorage.setItem(key, JSON.stringify(checkpointData))
      onProgress(100, `Checkpoint saved: ${name}`)
    } catch (e) {
      console.warn('Could not save checkpoint to localStorage:', e)
      onProgress(100, `Checkpoint created (not persisted): ${name}`)
    }

    return {
      success: true,
      checkpoint: name,
      timestamp: checkpointData.timestamp,
      description,
    }
  }
}

/**
 * Notify Executor - Send notification
 */
export const notifyExecutor = {
  async execute(config, context, onProgress = () => {}, signal = null) {
    const channel = config.channel || 'console'
    const recipient = config.recipient || ''
    const subject = config.subject || 'Workflow Notification'
    const message = config.message || 'Workflow step completed.'

    onProgress(0, `Sending notification via ${channel}...`)

    if (signal?.aborted) {
      throw new Error('Execution cancelled')
    }

    // Replace placeholders in message
    let finalMessage = message
      .replace('{{account}}', context.scout_account_id || '')
      .replace('{{application}}', context.application_name || '')
      .replace('{{timestamp}}', new Date().toISOString())

    let notificationResult = { sent: false, channel }

    switch (channel) {
      case 'console':
        console.log(`[NOTIFICATION] ${subject}: ${finalMessage}`)
        notificationResult = { sent: true, channel: 'console' }
        break

      case 'webhook':
        if (recipient) {
          try {
            const response = await fetch(recipient, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                subject,
                message: finalMessage,
                context: {
                  account: context.scout_account_id,
                  application: context.application_name,
                },
                timestamp: new Date().toISOString(),
              }),
              signal,
            })
            notificationResult = { sent: response.ok, channel: 'webhook', status: response.status }
          } catch (e) {
            notificationResult = { sent: false, channel: 'webhook', error: e.message }
          }
        }
        break

      case 'email':
      case 'slack':
        // These would need backend integration
        console.log(`[NOTIFICATION:${channel.toUpperCase()}] To: ${recipient}, Subject: ${subject}, Message: ${finalMessage}`)
        notificationResult = {
          sent: false,
          channel,
          message: `${channel} notifications require backend integration`,
        }
        break

      default:
        console.log(`[NOTIFICATION] ${subject}: ${finalMessage}`)
        notificationResult = { sent: true, channel: 'console' }
    }

    onProgress(100, `Notification ${notificationResult.sent ? 'sent' : 'logged'}`)

    return {
      success: true,
      ...notificationResult,
      subject,
      message: finalMessage,
    }
  }
}

/**
 * Custom Script Executor - Run custom code (sandboxed)
 *
 * Note: For security, this runs in a limited scope.
 * Only JavaScript is supported in browser environment.
 */
export const customScriptExecutor = {
  async execute(config, context, onProgress = () => {}, signal = null) {
    const language = config.language || 'javascript'
    const code = config.code || ''
    const timeout = (config.timeout || 30) * 1000

    onProgress(0, `Running custom ${language} script...`)

    if (signal?.aborted) {
      throw new Error('Execution cancelled')
    }

    if (language !== 'javascript') {
      return {
        success: false,
        error: `Language '${language}' not supported in browser. Use JavaScript.`,
        logs: [],
      }
    }

    if (!code.trim()) {
      return {
        success: true,
        result: null,
        logs: ['No code to execute'],
      }
    }

    // Create sandboxed execution environment
    const logs = []
    const sandboxConsole = {
      log: (...args) => logs.push({ level: 'log', message: args.map(String).join(' ') }),
      warn: (...args) => logs.push({ level: 'warn', message: args.map(String).join(' ') }),
      error: (...args) => logs.push({ level: 'error', message: args.map(String).join(' ') }),
      info: (...args) => logs.push({ level: 'info', message: args.map(String).join(' ') }),
    }

    // Limited context available to script
    const scriptContext = {
      account: context.scout_account_id,
      application: context.application_name,
      jobs: { ...context.jobs },
      // Get last result
      input: Object.values(context.results || {}).pop() || null,
    }

    try {
      // Create function with limited scope
      const AsyncFunction = Object.getPrototypeOf(async function(){}).constructor
      const fn = new AsyncFunction(
        'console',
        'context',
        'input',
        `
        ${code}
        `
      )

      // Execute with timeout
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Script timeout')), timeout)
      )

      const result = await Promise.race([
        fn(sandboxConsole, scriptContext, scriptContext.input),
        timeoutPromise,
      ])

      onProgress(100, 'Script completed')

      return {
        success: true,
        result: result !== undefined ? result : null,
        logs: logs.map(l => `[${l.level}] ${l.message}`),
      }
    } catch (error) {
      onProgress(100, `Script error: ${error.message}`)

      return {
        success: false,
        error: error.message,
        logs: logs.map(l => `[${l.level}] ${l.message}`),
      }
    }
  }
}

export default {
  wait: waitExecutor,
  condition: conditionExecutor,
  loop: loopExecutor,
  parallel: parallelExecutor,
  checkpoint: checkpointExecutor,
  notify: notifyExecutor,
  'custom-script': customScriptExecutor,
}
